"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidationUtils = void 0;
/*
 * Copyright (C) 2018 Spanning Cloud Apps.  All rights reserved.
 */
const _l = require("lodash");
/**
 * Utilities for simple validations.
 */
class ValidationUtils {
    /**
     * Asserts that a value is a finite number of at least a minimum value.
     *
     * @param value - the value to be tested.
     * @param min - the minimum value.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a finite number or its value does not meet the minimum.
     */
    static assertAtLeast(value, min, name) {
        ValidationUtils.assertIsFinite(value, name);
        ValidationUtils.assertIsFinite(min, 'min');
        if (value < min) {
            throw new Error(`Expected ${name} to be at least ${min} but found ${value}`);
        }
    }
    /**
     * Asserts that a value is a finite number of at most a maximum value.
     *
     * @param value - the value to be tested.
     * @param max - the maximum value.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a finite number or its value exceeds the maximum.
     */
    static assertAtMost(value, max, name) {
        ValidationUtils.assertIsFinite(value, name);
        ValidationUtils.assertIsFinite(max, 'max');
        if (value > max) {
            throw new Error(`Expected ${name} to be at most ${max} but found ${value}`);
        }
    }
    /**
     * Asserts that a value is one of the values in a defined set.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @param valueSet - the set of allowed values.
     * @throws if the value is not in the set.
     */
    static assertInSet(value, name, valueSet) {
        if (!_l.includes(valueSet, value)) {
            throw new Error(`Expected ${name} to be one of ${JSON.stringify(valueSet)} but found ${value}`);
        }
    }
    /**
     * Asserts that a value is an array.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not an array.
     */
    static assertIsArray(value, name) {
        ValidationUtils.assertType(value, name, 'an array', _l.isArray(value));
    }
    /**
     * Asserts that a value is an array or a function.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not an array or function.
     */
    static assertIsArrayOrFn(value, name) {
        ValidationUtils.assertType(value, name, 'an array', _l.isArray(value) || _l.isFunction(value));
    }
    /**
     * Asserts that a value is a Boolean.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a Boolean.
     */
    static assertIsBoolean(value, name) {
        ValidationUtils.assertType(value, name, 'a boolean', _l.isBoolean(value));
    }
    /**
     * Asserts that a value is a Boolean or a function.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a Boolean or a function.
     */
    static assertIsBooleanOrFn(value, name) {
        ValidationUtils.assertType(value, name, 'a boolean', _l.isBoolean(value) || _l.isFunction(value));
    }
    /**
     * Asserts that a value is one of the values in an enum.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @param enumObj - the enum to be checked. Should be a keyed object with values of the same type as the
     * value to be checked.
     * @throws if the value is not a finite number.
     */
    static assertIsEnumValue(value, name, enumObj) {
        ValidationUtils.assertInSet(value, name, _l.values(enumObj));
    }
    /**
     * Asserts that a value is a finite number.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a finite number.
     */
    static assertIsFinite(value, name) {
        ValidationUtils.assertType(value, name, 'a finite number', _l.isFinite(value));
    }
    /**
     * Asserts that a value is a finite number or a function.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a finite number or a function.
     */
    static assertIsFiniteOrFn(value, name) {
        ValidationUtils.assertType(value, name, 'a finite number', _l.isFinite(value) || _l.isFunction(value));
    }
    /**
     * Asserts that a value is a function.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a function.
     */
    static assertIsFunction(value, name) {
        ValidationUtils.assertType(value, name, 'a function', _l.isFunction(value));
    }
    /**
     * Asserts that a value is an object, but not an array or function.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not an object.
     */
    static assertIsObject(value, name) {
        ValidationUtils.assertType(value, name, 'an object', ValidationUtils.isPlainObject(value));
    }
    /**
     * Asserts that a value is an object (but not an array) or a function.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not an object or a function.
     */
    static assertIsObjectOrFn(value, name) {
        ValidationUtils.assertType(value, name, 'an object', ValidationUtils.isPlainObject(value) || _l.isFunction(value));
    }
    /**
     * Asserts that a value is a string.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a string.
     */
    static assertIsString(value, name) {
        ValidationUtils.assertType(value, name, 'a string', _l.isString(value));
    }
    /**
     * Asserts that a value is a string or a function.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @throws if the value is not a string or a function.
     */
    static assertIsStringOrFn(value, name) {
        ValidationUtils.assertType(value, name, 'a string', _l.isString(value) || _l.isFunction(value));
    }
    /**
     * Asserts that a test of a value's type was true.
     *
     * @param value - the value to be tested.
     * @param name - the name to be used when describing the value in the error message.
     * @param expectedType - the expected type to be used in the error message.
     * @param result - the result of the type test
     * @throws if the result was false.
     */
    static assertType(value, name, expectedType, result) {
        if (!result) {
            const type = _l.isNull(value) ? 'null' : typeof value;
            value = _l.isNil(value) || _l.isObject(value) ? '' : `: ${_l.toString(value)}`;
            throw new Error(`Expected ${name} to be ${expectedType} but found ${type}${value}`);
        }
    }
    /**
     * Determines whether a value is an object, but not an array or function. I thought we could replace this with
     * lodash's _.isPlainObject, but that has not quite the same behavior.
     *
     * @param value - the value to be tested.
     * @returns true if the value is a plain object, false otherwise.
     */
    static isPlainObject(value) {
        return _l.isObject(value) && !_l.isArray(value) && !_l.isFunction(value);
    }
}
exports.ValidationUtils = ValidationUtils;
//# sourceMappingURL=ValidationUtils.js.map